import { useSession } from "@/services/auth";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import type { OptionEstimateReasons, OptionEstimates } from "@/api.types.v2";

const API_URL = import.meta.env.VITE_API_URL;

export interface OptionEstimateInput {
  /** Opaque client option key, echoed back in the response map. */
  key: string;
  carrierServiceId: string;
  /** The option's pickup slots (same ids shown in its slot picker). */
  slotIds: string[];
  /** Quote-selected dispatch for options without pickup slots. */
  dispatchCenterId?: string;
  handoffDispatchCenterId?: string;
}

interface OptionEstimatesResponse {
  data: {
    estimates: Record<string, OptionEstimates | null>;
    estimateReasons: Record<string, OptionEstimateReasons | null>;
  };
}

/**
 * Date-aware delivery estimates for ALL options on a chosen pickup date.
 *
 * The quote (`get-quote`) computes each option's estimate against the earliest
 * bookable date (the date is unknown at quote time). Once the customer picks a
 * pickup date in the pricing step, this refreshes every option card's estimate
 * for that date via POST /api/v2/pricing/option-estimates (no rate recompute).
 *
 * Returns date-specific estimates and their failure reasons. Disabled (no request)
 * until there is a date and at least one option.
 */
export function useOptionEstimates(
  date: string,
  options: OptionEstimateInput[]
) {
  const { token } = useSession();
  // Stable key so identical inputs don't refetch.
  const optionsKey = JSON.stringify(
    options.map((o) => [
      o.key,
      o.carrierServiceId,
      [...o.slotIds].sort(),
      o.dispatchCenterId,
      o.handoffDispatchCenterId,
    ])
  );

  return useQuery({
    queryKey: ["option-estimates", date, optionsKey],
    enabled: !!token && !!date && options.length > 0,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await axios.post<OptionEstimatesResponse>(
        `${API_URL}/api/v2/pricing/option-estimates`,
        { date, options },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
  });
}
